import React, { useState, useRef, useEffect } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { FlexyIconButton } from '@europrocurement/flexy-components';
import { editIcon } from '@europrocurement/l2d-icons';
import { useEditSousClient } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';

interface HeaderTitleProps {
    clientOpereRaisonSociale: string;
    prescriberId?: number;
    sousClientId?: number;
    onSuccess?: () => void;
}

export const HeaderTitle: React.FunctionComponent<HeaderTitleProps> = function ({
    clientOpereRaisonSociale,
    prescriberId,
    sousClientId,
    onSuccess,
}) {
    const [titleHovered, setTitleHovered] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const titleRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const el = titleRef.current;
        if (!el) return undefined;

        const checkTruncation = () => setIsTruncated(el.scrollWidth > el.clientWidth);
        checkTruncation();

        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(el);
        return () => resizeObserver.disconnect();
    }, []);

    const { editFolio } = useEditSousClient({
        sousClientId,
        onSuccess: () => onSuccess?.(),
    });

    const titleWrapperStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '0 8px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginBottom: '0',
    };

    const titleStyle = {
        overflow: 'hidden',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
        minWidth: 0,
    };

    const prescriberIsClient = prescriberId !== undefined && prescriberId === sousClientId;
    const title = (
        <Tooltip
            title={clientOpereRaisonSociale}
            disableHoverListener={!isTruncated}
            disableFocusListener={!isTruncated}
            disableTouchListener={!isTruncated}
            enterTouchDelay={0}
            leaveTouchDelay={1500}
        >
            <Typography
                ref={titleRef}
                variant="l2dh5"
                sx={titleStyle}
            >
                {clientOpereRaisonSociale}
            </Typography>
        </Tooltip>
    );

    return (
        <Box sx={titleWrapperStyle}>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minHeight: '24px',
                    minWidth: 0,
                }}
                onMouseEnter={() => setTitleHovered(true)}
                onMouseLeave={() => setTitleHovered(false)}
            >
                {prescriberIsClient && (
                    <Tooltip title="Pour modifier le prescripteur, rendez vous sur sa fiche">
                        <Typography
                            variant="l2dh5"
                            sx={titleStyle}
                        >
                            {clientOpereRaisonSociale}
                        </Typography>
                    </Tooltip>
                )}
                {!prescriberIsClient && (
                    <>
                        {title}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '4px',
                                flexShrink: 0,
                                opacity: isTruncated || titleHovered ? 1 : 0,
                                transform:
                                    isTruncated || titleHovered
                                        ? 'translateX(0)'
                                        : 'translateX(-10px)',
                                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                            }}
                        >
                            <FlexyIconButton
                                displayTooltip
                                icon={editIcon}
                                onClick={editFolio}
                                tooltipOverwriteProps={{
                                    title: 'Éditer la société opérée',
                                }}
                                iconOverwriteProps={{
                                    size: '2xs',
                                    sx: {
                                        color: { xs: 'text.secondary', md: 'inherit' },
                                        '&:hover': {
                                            color: 'primary.main',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};
